import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search, X, MessagesSquare, CheckCircle2 } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  CARE_COMMUNICATION_DIRECTIONS,
  CARE_COMMUNICATION_STATUSES,
  useCareCommunicationsList,
  type CareCommunicationDirection,
  type CareCommunicationListFilters,
  type CareCommunicationListItem,
  type CareCommunicationStatus,
} from '@/hooks/useCareCommunications';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

export function CareCommunications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<CareCommunicationStatus | 'all'>('all');
  const [directionFilter, setDirectionFilter] = useState<CareCommunicationDirection | 'all'>(
    'all',
  );
  const [searchTerm, setSearchTerm] = useState('');

  const filters = useMemo<CareCommunicationListFilters>(
    () => ({
      status: statusFilter,
      direction: directionFilter,
      search: searchTerm.trim() || undefined,
    }),
    [statusFilter, directionFilter, searchTerm],
  );

  const { data, isLoading, error } = useCareCommunicationsList(activeClinicId, filters);
  const items = data?.items ?? [];

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:tracking-tight">
                {t('communicate.title')}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('communicate.subtitle')}
              </p>
            </div>
            <div className="mt-4 flex md:ml-4 md:mt-0">
              <Button
                onClick={() => navigate('/care-communications/new')}
                disabled={!activeClinicId}
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('communicate.new_message')}
              </Button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('communicate.search_placeholder')}
                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm leading-5 placeholder-gray-500 focus:border-indigo-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
                  aria-label={t('communicate.clear_search')}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="inline-flex flex-wrap rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800">
              {CARE_COMMUNICATION_DIRECTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDirectionFilter(d)}
                  className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                    directionFilter === d
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(`communicate.direction_${d}`)}
                </button>
              ))}
            </div>

            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as CareCommunicationStatus | 'all')
              }
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              {CARE_COMMUNICATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`communicate.status_${s}`)}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="mt-6">
              <ErrorAlert message={t('communicate.load_error')} />
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState hasSearch={Boolean(searchTerm)} />
          ) : (
            <div className="mt-6 overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <Th>{t('communicate.col_topic')}</Th>
                      <Th>{t('communicate.col_category')}</Th>
                      <Th>{t('communicate.col_sender')}</Th>
                      <Th>{t('communicate.col_receiver')}</Th>
                      <Th>{t('communicate.col_patient')}</Th>
                      <Th>{t('communicate.col_date')}</Th>
                      <Th>{t('communicate.col_status')}</Th>
                      <Th>{t('communicate.col_ack')}</Th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">{t('communicate.col_actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                    {items.map((item) => (
                      <tr
                        key={item.id}
                        onClick={() =>
                          navigate(
                            `/care-communications/${encodeURIComponent(item.communicationIdentifier)}`,
                          )
                        }
                        className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <td className="max-w-xs truncate px-3 py-4 text-sm text-gray-900 dark:text-white first:pl-4 sm:first:pl-6">
                          {item.topic?.trim() ||
                            item.communicationIdentifier.replace(/^urn:uuid:/i, '').slice(0, 12) +
                              '…'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {item.category
                            ? t(`communicate.category_${item.category}`, {
                                defaultValue: item.category,
                              })
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {item.senderSor ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {item.receiverSor ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {item.has_cpr ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                              CPR
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {formatMessageDate(item)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <AckBadge item={item} />
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/care-communications/${encodeURIComponent(item.communicationIdentifier)}`,
                              );
                            }}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            {t('communicate.see_thread')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function formatMessageDate(item: CareCommunicationListItem): string {
  const iso = item.direction === 'in' ? item.receivedAt ?? item.createdAt : item.sentAt ?? item.createdAt;
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK');
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-4 dark:text-white first:sm:pl-6"
    >
      {children}
    </th>
  );
}

export function StatusBadge({ status }: { status: CareCommunicationStatus }) {
  const { t } = useTranslation();
  const cls: Partial<Record<CareCommunicationStatus, string>> = {
    received: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    validated: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
    queued: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    sent: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
    delivered: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    acknowledged: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    failed: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    rejected: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        cls[status] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
      }`}
    >
      {t(`communicate.status_${status}`)}
    </span>
  );
}

export function AckBadge({ item }: { item: CareCommunicationListItem }) {
  const { t } = useTranslation();
  const ack = item.latestAcknowledgement;
  if (!ack) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }
  const cls: Record<string, string> = {
    AA: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    AE: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    AR: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        cls[ack.ackType] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
      }`}
      title={ack.errorMessage ?? undefined}
    >
      {t(`communicate.ack_${ack.ackType}`, { defaultValue: ack.ackType })}
    </span>
  );
}

function EmptyState({ hasSearch }: { hasSearch: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mt-12 text-center">
      <MessagesSquare className="mx-auto h-10 w-10 text-gray-400" />
      <h3 className="mt-4 text-base font-medium text-gray-900 dark:text-white">
        {hasSearch ? t('communicate.empty_search_title') : t('communicate.empty_title')}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {hasSearch ? t('communicate.empty_search_subtitle') : t('communicate.empty_subtitle')}
      </p>
    </div>
  );
}
